/**
 * High-resolution timing utilities
 */
export class Time {
  private startTime: number;
  private lastFrameTime: number;
  private deltaTime: number = 0;
  private frameCount: number = 0;
  private fpsUpdateInterval: number = 500; // ms
  private lastFpsUpdate: number = 0;
  private framesInInterval: number = 0;
  private currentFps: number = 0;

  constructor() {
    this.startTime = performance.now();
    this.lastFrameTime = this.startTime;
    this.lastFpsUpdate = this.startTime;
  }

  /**
   * Call at the start of each frame
   * Returns delta time in seconds
   */
  tick(): number {
    const now = performance.now();
    this.deltaTime = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;
    this.frameCount++;
    this.framesInInterval++;

    // Update FPS counter
    if (now - this.lastFpsUpdate >= this.fpsUpdateInterval) {
      this.currentFps = (this.framesInInterval * 1000) / (now - this.lastFpsUpdate);
      this.framesInInterval = 0;
      this.lastFpsUpdate = now;
    }

    return this.deltaTime;
  }

  /**
   * Get current time in seconds since start
   */
  now(): number {
    return (performance.now() - this.startTime) / 1000;
  }

  /**
   * Get delta time from last frame in seconds
   */
  getDeltaTime(): number {
    return this.deltaTime;
  }

  /**
   * Get current FPS (smoothed)
   */
  getFps(): number {
    return Math.round(this.currentFps);
  }

  /**
   * Get total frame count
   */
  getFrameCount(): number {
    return this.frameCount;
  }

  /**
   * Get elapsed time since start in seconds
   */
  getElapsedTime(): number {
    return (performance.now() - this.startTime) / 1000;
  }
}

/**
 * Simple timer for cooldowns and delays
 */
export class Timer {
  private duration: number;
  private remaining: number;
  private running: boolean = false;

  constructor(duration: number = 0) {
    this.duration = duration;
    this.remaining = 0;
  }

  /**
   * Start the timer
   */
  start(duration?: number): void {
    if (duration !== undefined) {
      this.duration = duration;
    }
    this.remaining = this.duration;
    this.running = true;
  }

  /**
   * Update the timer
   * @param dt Delta time in seconds
   * @returns true if timer just finished this frame
   */
  update(dt: number): boolean {
    if (!this.running) return false;

    const wasAboveZero = this.remaining > 0;
    this.remaining -= dt;

    if (this.remaining <= 0) {
      this.remaining = 0;
      this.running = false;
      return wasAboveZero;
    }

    return false;
  }

  /**
   * Check if timer is done
   */
  isDone(): boolean {
    return !this.running && this.remaining <= 0;
  }

  /**
   * Check if timer is running
   */
  isRunning(): boolean {
    return this.running;
  }

  /**
   * Get remaining time
   */
  getRemaining(): number {
    return this.remaining;
  }

  /**
   * Get progress (0 to 1)
   */
  getProgress(): number {
    if (this.duration === 0) return 1;
    return 1 - this.remaining / this.duration;
  }

  /**
   * Reset the timer without starting
   */
  reset(): void {
    this.remaining = this.duration;
    this.running = false;
  }

  /**
   * Stop the timer
   */
  stop(): void {
    this.running = false;
  }
}
